import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { flattenToAppURL } from '@plone/volto/helpers';
import moment from 'moment';
import processItemsForRecurrence from './processItemsForRecurrence';
import './Advanced.css';

const PencilIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const AdvancedCitationBlockTemplate = ({
  items,
  effectiveDate,
  showRecurrence,
}) => {
  const isLoggedIn = useSelector(
    (state) => !!state.userSession?.token,
  );

  const processedItems = useMemo(() => {
    return showRecurrence
      ? processItemsForRecurrence(items)
      : items.map((item) => ({
          ...item,
          url: flattenToAppURL(item['@id']),
        }));
  }, [items, showRecurrence]);

  return (
    <div className="citation-block">
      {processedItems.map((item) => {
        // Use the new 'authors' field, fallback to creators if not available
        let authorsList = [];

        if (item.authors && item.authors.trim()) {
          authorsList = item.authors.split(',').map((a) => a.trim());
        } else if (item.listCreators && item.listCreators.length > 0) {
          authorsList = item.listCreators;
        } else if (item.Creator) {
          authorsList = [item.Creator];
        }

        const itemUrl = flattenToAppURL(item['@id']);

        return (
          <p className="citation" key={item['@id']}>
            {/* ✅ Author(s) with et al tooltip */}
            {authorsList.length > 0 &&
              (authorsList.length > 6 ? (
                <>
                  {authorsList.slice(0, 6).join(', ')}
                  {', '}
                  <span
                    title={authorsList.slice(6).join(', ')}
                    style={{ textDecoration: 'underline dotted', cursor: 'help' }}
                  >
                    et al.
                  </span>{' '}
                </>
              ) : (
                <>{authorsList.join(', ')} </>
              ))}

            {/* ✅ Year in brackets */}
            {effectiveDate && item.effective && (
              <>({moment(item.effective).format('YYYY')}). </>
            )}

            {/* ✅ Title with conditional period */}
            <strong>{item.title}</strong>
            {item.title && !item.title.trim().endsWith('.') ? '.' : ''}{' '}

            {/* ✅ Link – use getRemoteUrl for Link items */}
            {item['@id'] && (
              <a
                href={item.getRemoteUrl || item['@id']}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item['@type'] === 'Link' && item.getRemoteUrl
                  ? item.getRemoteUrl
                  : 'Read more'}
              </a>
            )}

            {/* ✅ Logged-in-only shortcut — opens item in new tab for toolbar editing */}
            {isLoggedIn && item['@id'] && (
              <a
                href={itemUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open to edit: ${item.title}`}
                className="citation-edit-link"
                aria-label={`Open ${item.title} in new tab`}
              >
                <PencilIcon />
              </a>
            )}
          </p>
        );
      })}
    </div>
  );
};

AdvancedCitationBlockTemplate.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  effectiveDate: PropTypes.bool,
  showRecurrence: PropTypes.bool,
};

export default React.memo(AdvancedCitationBlockTemplate);
